import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useAppSelector } from "../../../../hooks/store-hooks"
import { useLimitacaoConsumoService } from "../../../../../use-cases/limitacao-consumo"
import { useUIService } from "../../../../../use-cases/ui"
import { useNavigationService } from "../../../../../use-cases/navigation"
import { useProdutoLimitacaoConsumoApi } from "../../../../../infra/produto-limitacao-consumo"
import { AppBar, Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, FormControl, FormControlLabel, FormGroup, FormLabel, Grid, Stack, TextField, ToggleButton, ToggleButtonGroup, Toolbar, Typography } from "@mui/material"
import { onlyNumbers } from "../../../../../utils"
import { ILimitacaoConsumoTable, IProdutoLimitacaoConsumoDetails, IProdutoTipoConsumoLimitacaoConsumoAtivo } from "../../../../../application/models"
import CloseIcon from '@mui/icons-material/Close'
import CategoryIcon from '@mui/icons-material/Category'
import ChecklistIcon from '@mui/icons-material/Checklist'
import LayersIcon from '@mui/icons-material/Layers'

export const FormCreateTravaConsumoPerfil = () => {
    const { createLimitacaoConsumoPerfilForm, produtosList } = useAppSelector((state) => state.limitacaoConsumo)

    const [loading, setLoading] = useState<boolean>(true)
    const [travasAtivas, setTravasAtivas] = useState<ILimitacaoConsumoTable[]>([])
    const [idProdutoParaVariaveis, setIdProdutoParaVariaveis] = useState(0)
    const [variaveisDoProdutoSelecionado, setVariaveisDoProdutoSelecionado] = useState<IProdutoTipoConsumoLimitacaoConsumoAtivo[]>([])
    const [loadingVariaveis, setLoadingVariaveis] = useState(false)
    const [searchTermVariaveis, setSearchTermVariaveis] = useState("")
    const [modo, setModo] = useState<'produto' | 'variavel'>('produto')

    const isFirstRender = useRef(false)

    const {
        setCreateLimitacaoConsumoPerfilOpenForm,
        setCreateLimitacaoConsumoPerfilFormIdProduto,
        setCreateLimitacaoConsumoPerfilFormIdTipoConsumo,
        setCreateLimitacaoConsumoPerfilFormIsGrupo,
        setCreateLimitacaoConsumoPerfilFormQuantidade,
        submitCreateLimitacaoConsumoPerfil,
        getProdutoLimitacaoConsumoList,
        findAllActiveLimitacaoConsumoPerfil
    } = useLimitacaoConsumoService()

    const { setAlert } = useUIService()
    const { navigateTo } = useNavigationService()
    const { produtoLimitacaoConsumoGetTipoConsumoAtivosApi } = useProdutoLimitacaoConsumoApi()

    useEffect(() => {
        getProdutoLimitacaoConsumoList()
    }, [getProdutoLimitacaoConsumoList])

    useEffect(() => {
        const buscarTravasAtivas = async () => {
            if (createLimitacaoConsumoPerfilForm.fields.IdPerfil && createLimitacaoConsumoPerfilForm.fields.IdPerfil > 0) {
                const travas = await findAllActiveLimitacaoConsumoPerfil(createLimitacaoConsumoPerfilForm.fields.IdPerfil)
                setTravasAtivas(travas)
            }
        }
        void buscarTravasAtivas()
    }, [createLimitacaoConsumoPerfilForm.fields.IdPerfil, findAllActiveLimitacaoConsumoPerfil])

    useEffect(() => {
        if (modo !== 'variavel') {
            setIdProdutoParaVariaveis(0)
            setVariaveisDoProdutoSelecionado([])
            setSearchTermVariaveis("")
            if (createLimitacaoConsumoPerfilForm.fields.IdTipoConsumo.length > 0) {
                setCreateLimitacaoConsumoPerfilFormIdTipoConsumo({ IdTipoConsumo: [] })
            }
        } else {
            if (createLimitacaoConsumoPerfilForm.fields.IdProduto > 0) {
                setCreateLimitacaoConsumoPerfilFormIdProduto({ IdProduto: 0 })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modo])

    useEffect(() => {
        if (modo !== 'variavel' || idProdutoParaVariaveis <= 0) {
            setVariaveisDoProdutoSelecionado([])
            return
        }

        let cancelled = false
        setLoadingVariaveis(true)

        void (async () => {
            try {
                const list = await produtoLimitacaoConsumoGetTipoConsumoAtivosApi(idProdutoParaVariaveis)
                if (!cancelled) setVariaveisDoProdutoSelecionado(list)
            } catch {
                if (!cancelled) {
                    setVariaveisDoProdutoSelecionado([])
                    setAlert({
                        message: "Não foi possível carregar as variáveis deste produto.",
                        type: "error"
                    })
                }
            } finally {
                if (!cancelled) setLoadingVariaveis(false)
            }
        })()

        return () => { cancelled = true }
    }, [modo, idProdutoParaVariaveis, produtoLimitacaoConsumoGetTipoConsumoAtivosApi, setAlert])

    useEffect(() => {
        if (!isFirstRender.current) {
            setLoading(false)
            isFirstRender.current = true

            if (!createLimitacaoConsumoPerfilForm.fields.IdPerfil || createLimitacaoConsumoPerfilForm.fields.IdPerfil === 0) {
                setAlert({
                    message: 'Você deve selecionar um perfil',
                    type: 'error'
                })
                navigateTo({ route: '/dashboard/trava/consumo' })
            }
        }
    }, [createLimitacaoConsumoPerfilForm.fields.IdPerfil, navigateTo, setAlert])

    const produtoTemTravaGeral = useCallback(
        (idProduto: number) =>
            travasAtivas.some((t) => t.IdTipoConsumo == null && t.IdProduto != null && Number(t.IdProduto) === Number(idProduto)),
        [travasAtivas]
    )

    const variavelTemTravaAtiva = useCallback(
        (idTipoConsumo: number) =>
            travasAtivas.some((t) => t.IdTipoConsumo != null && Number(t.IdTipoConsumo) === Number(idTipoConsumo)),
        [travasAtivas]
    )

    const alternarVariavelSelecionada = useCallback((idTipoConsumo: number, checked: boolean) => {
        if (checked && variavelTemTravaAtiva(idTipoConsumo)) return
        const atual = new Set(createLimitacaoConsumoPerfilForm.fields.IdTipoConsumo)
        if (checked) atual.add(idTipoConsumo)
        else atual.delete(idTipoConsumo)
        setCreateLimitacaoConsumoPerfilFormIdTipoConsumo({ IdTipoConsumo: Array.from(atual) })
    }, [createLimitacaoConsumoPerfilForm.fields.IdTipoConsumo, setCreateLimitacaoConsumoPerfilFormIdTipoConsumo, variavelTemTravaAtiva])

    const handleClose = () => {
        setCreateLimitacaoConsumoPerfilOpenForm({ open: false })
    }

    const produtoSelecionadoParaVariaveis = produtosList.find((p) => p.IdProduto === idProdutoParaVariaveis) ?? null

    const produtosOrdenados = useMemo(() => [...produtosList].sort((a, b) => a.Descricao.localeCompare(b.Descricao)), [produtosList])

    return (
        loading ? <></> : (
            <Dialog
                open={true}
                fullScreen
                disableEscapeKeyDown
                onClose={handleClose}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
                aria-describedby="scroll-dialog-description"
            >
                <AppBar sx={{ position: 'relative' }}>
                    <Toolbar>
                        <Typography sx={{ ml: 2, flex: 1 }} textTransform={"uppercase"} fontWeight={"bold"} variant="h6" component="div">
                            Criar trava de consumo (perfil)
                        </Typography>
                        <Button
                            color="inherit"
                            startIcon={<CloseIcon />}
                            onClick={handleClose}
                            aria-label="close"
                        >
                            Fechar
                        </Button>
                    </Toolbar>
                </AppBar>
                <DialogContent dividers={true}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                value={
                                    createLimitacaoConsumoPerfilForm.fields.Quantidade === 0 ? ''
                                        : createLimitacaoConsumoPerfilForm.fields.Quantidade.toLocaleString('pt-BR')
                                }
                                onChange={(e) => {
                                    setCreateLimitacaoConsumoPerfilFormQuantidade({
                                        Quantidade: Number(onlyNumbers(e.target.value))
                                    })
                                }}
                                InputLabelProps={{ shrink: true }}
                                name="quantidade"
                                fullWidth
                                id="quantidade"
                                label="Quantidade"
                                autoFocus
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold', color: 'text.primary', display: "block" }}>
                                Modalidade da Trava
                            </FormLabel>
                            <ToggleButtonGroup
                                exclusive
                                fullWidth
                                value={modo}
                                onChange={(_e, value) => {
                                    if (value != null) setModo(value)
                                }}
                            >
                                <ToggleButton value="produto" sx={{ py: 1.2, gap: 0.8, fontWeight: 'bold', fontSize: '0.82rem' }}>
                                    <CategoryIcon fontSize="small" /> Por produto
                                </ToggleButton>
                                <ToggleButton value="variavel" sx={{ py: 1.2, gap: 0.8, fontWeight: 'bold', fontSize: '0.82rem' }}>
                                    <ChecklistIcon fontSize="small" /> Por variável
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Grid>

                        {modo === 'produto' && (
                            <Grid item xs={12}>
                                <Autocomplete
                                    fullWidth
                                    autoComplete
                                    includeInputInList
                                    options={produtosOrdenados}
                                    getOptionLabel={(option) => option.Descricao.toUpperCase()}
                                    getOptionDisabled={(option) => produtoTemTravaGeral(option.IdProduto)}
                                    value={produtosList.find((produto) => produto.IdProduto === createLimitacaoConsumoPerfilForm.fields.IdProduto) ?? null}
                                    onChange={(_event: unknown, newValue: IProdutoLimitacaoConsumoDetails | null) => {
                                        if (newValue) {
                                            if (produtoTemTravaGeral(newValue.IdProduto)) {
                                                setAlert({
                                                    message: 'Este produto já possui uma trava de consumo geral ativa para este perfil.',
                                                    type: 'error'
                                                })
                                                return
                                            }
                                            setCreateLimitacaoConsumoPerfilFormIdProduto({ IdProduto: newValue.IdProduto })
                                        } else {
                                            setCreateLimitacaoConsumoPerfilFormIdProduto({ IdProduto: 0 })
                                        }
                                    }}
                                    renderOption={(props, option) => {
                                        const temTravaGeral = produtoTemTravaGeral(option.IdProduto)
                                        return (
                                            <li {...props} style={{
                                                ...props.style,
                                                opacity: temTravaGeral ? 0.5 : 1,
                                                cursor: temTravaGeral ? 'not-allowed' : 'pointer'
                                            }}>
                                                {option.Descricao.toUpperCase()}
                                                {temTravaGeral && ' (Trava geral já ativa)'}
                                            </li>
                                        )
                                    }}
                                    renderInput={(params) =>
                                        <TextField {...params} label="Selecione um produto" />
                                    }
                                />
                            </Grid>
                        )}

                        {modo === 'variavel' && (
                            <>
                                <Grid item xs={12}>
                                    <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'action.hover', border: '1px solid', borderColor: 'divider', mb: 1 }}>
                                        <Typography variant="caption" fontWeight="bold" color="text.secondary" textTransform="uppercase" sx={{ display: 'block', mb: 1 }}>
                                            Formato da trava por variável
                                        </Typography>
                                        <ToggleButtonGroup
                                            exclusive
                                            fullWidth
                                            size="small"
                                            value={createLimitacaoConsumoPerfilForm.fields.IsGrupo ? "grupo" : "individual"}
                                            onChange={(_e, value) => {
                                                if (value != null) {
                                                    setCreateLimitacaoConsumoPerfilFormIsGrupo({
                                                        IsGrupo: value === "grupo"
                                                    })
                                                }
                                            }}
                                        >
                                            <ToggleButton value="individual" sx={{ py: 0.8, gap: 0.8, fontWeight: 'bold', fontSize: '0.78rem' }}>
                                                <ChecklistIcon fontSize="small" /> Travas Individuais
                                            </ToggleButton>
                                            <ToggleButton value="grupo" sx={{ py: 0.8, gap: 0.8, fontWeight: 'bold', fontSize: '0.78rem' }}>
                                                <LayersIcon fontSize="small" /> Trava em Grupo (Compartilhada)
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        sx={{ mb: 1 }}
                                    >
                                        <Typography variant="subtitle1" fontWeight="bold" color="primary" textTransform="uppercase">
                                            {createLimitacaoConsumoPerfilForm.fields.IsGrupo ? "Variáveis do Grupo (Limite Compartilhado)" : "Variáveis (Travas Separadas)"}
                                        </Typography>
                                        <Chip
                                            icon={createLimitacaoConsumoPerfilForm.fields.IsGrupo ? <LayersIcon style={{ fontSize: '0.9rem' }} /> : <ChecklistIcon style={{ fontSize: '0.9rem' }} />}
                                            label={createLimitacaoConsumoPerfilForm.fields.IsGrupo ? "GRUPO COMPARTILHADO" : "TRAVAS SEPARADAS"}
                                            color={createLimitacaoConsumoPerfilForm.fields.IsGrupo ? "primary" : "default"}
                                            size="small"
                                            sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}
                                        />
                                    </Stack>
                                    <Autocomplete
                                        fullWidth
                                        autoComplete
                                        includeInputInList
                                        options={produtosOrdenados}
                                        getOptionLabel={(option) => option.Descricao.toUpperCase()}
                                        value={produtoSelecionadoParaVariaveis}
                                        onChange={(_event: unknown, newValue: IProdutoLimitacaoConsumoDetails | null) => {
                                            const id = newValue?.IdProduto ?? 0
                                            setIdProdutoParaVariaveis(id)
                                            setSearchTermVariaveis("")
                                            setCreateLimitacaoConsumoPerfilFormIdProduto({ IdProduto: id })
                                            setCreateLimitacaoConsumoPerfilFormIdTipoConsumo({ IdTipoConsumo: [] })
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Selecione o produto para listar as variáveis"
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    {idProdutoParaVariaveis > 0 && loadingVariaveis && (
                                        <Typography variant="body2" color="text.secondary">Carregando variáveis...</Typography>
                                    )}
                                    {idProdutoParaVariaveis > 0 && !loadingVariaveis && variaveisDoProdutoSelecionado.length === 0 && (
                                        <Typography variant="body2" color="text.secondary">Nenhuma variável disponível para este produto.</Typography>
                                    )}
                                    {idProdutoParaVariaveis > 0 && !loadingVariaveis && variaveisDoProdutoSelecionado.length > 0 && (
                                        <FormControl component="fieldset" fullWidth>
                                            <TextField
                                                size="small"
                                                placeholder="Pesquisar variável..."
                                                variant="outlined"
                                                fullWidth
                                                sx={{ mb: 1 }}
                                                value={searchTermVariaveis}
                                                onChange={(e) => setSearchTermVariaveis(e.target.value)}
                                            />
                                            <FormGroup sx={{ pl: 2 }}>
                                                {variaveisDoProdutoSelecionado
                                                    .filter((tc) =>
                                                        !searchTermVariaveis ||
                                                        tc.Descricao.toLowerCase().includes(searchTermVariaveis.toLowerCase())
                                                    )
                                                    .map((variavel) => {
                                                        const bloqueada = variavelTemTravaAtiva(variavel.IdTipoConsumo)
                                                        return (
                                                            <FormControlLabel
                                                                key={variavel.IdTipoConsumo}
                                                                disabled={bloqueada}
                                                                control={
                                                                    <Checkbox
                                                                        checked={createLimitacaoConsumoPerfilForm.fields.IdTipoConsumo.some((id) => Number(id) === Number(variavel.IdTipoConsumo))}
                                                                        disabled={bloqueada}
                                                                        onChange={(event) => {
                                                                            alternarVariavelSelecionada(variavel.IdTipoConsumo, event.target.checked)
                                                                        }}
                                                                    />
                                                                }
                                                                label={`${variavel.Descricao}${bloqueada ? ' (já possui trava ativa)' : ''}`}
                                                            />
                                                        )
                                                    })}
                                            </FormGroup>
                                        </FormControl>
                                    )}
                                </Grid>
                            </>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancelar</Button>
                    <Button onClick={submitCreateLimitacaoConsumoPerfil} variant="contained">Criar trava de consumo</Button>
                </DialogActions>
            </Dialog>
        )
    )
}

